import { formatNumberWithTooltip } from 'components/tooltip-number';
import style from './unit-interest-card.module.scss';

export interface AcreageUnitInterestFormula {
  tractNri: number;
  wi?: number;
  uatAc: number;
  uatGisAc: number;
  unitTractGrossAcresAc: number;
  participationTractAc: string;
}

export interface LateralUnitInterestFormula {
  tractNri: number;
  wi?: number;
  uatFt: number;
  totalLlFt: number | null;
  participationTractAc: string;
  uatGisAc: number;
}

export function getUnitNRIUnroundedFormula(
  activeAllocationMethod: 'acreage' | 'lateral_length',
  formulaMetadata: AcreageUnitInterestFormula | LateralUnitInterestFormula,
  unitInterest: number
): string {
  const tractNri = formulaMetadata.tractNri ?? 0;
  const participationTractAc =
    'participationTractAc' in formulaMetadata
      ? typeof formulaMetadata.participationTractAc === 'string'
        ? parseFloat(formulaMetadata.participationTractAc)
        : (formulaMetadata.participationTractAc ?? 0)
      : 0;
  const uatGisAc = formulaMetadata.uatGisAc ?? 0;

  if (activeAllocationMethod === 'lateral_length') {
    const totalLlFt =
      (formulaMetadata as LateralUnitInterestFormula).totalLlFt ?? 0;
    const uatFt = (formulaMetadata as LateralUnitInterestFormula).uatFt ?? 0;

    return `${tractNri} * (${uatFt} / ${totalLlFt}) * (${participationTractAc} / ${uatGisAc}) = ${unitInterest}`;
  } else {
    const uatAc = (formulaMetadata as AcreageUnitInterestFormula).uatAc ?? 0;
    const unitTractGrossAcresAc =
      (formulaMetadata as AcreageUnitInterestFormula).unitTractGrossAcresAc ??
      0;

    return `${tractNri} * (${uatAc} / ${unitTractGrossAcresAc}) * (${participationTractAc} / ${uatGisAc}) = ${unitInterest}`;
  }
}

export function buildUnitNRICalculation(
  activeAllocationMethod: 'acreage' | 'lateral_length',
  formulaMetadata: AcreageUnitInterestFormula | LateralUnitInterestFormula,
  unitInterest: number,
  isWI: boolean = false
): JSX.Element {
  const tractNri = (!isWI ? formulaMetadata.tractNri : formulaMetadata.wi) ?? 0;
  const participationTractAc =
    'participationTractAc' in formulaMetadata
      ? typeof formulaMetadata.participationTractAc === 'string'
        ? parseFloat(formulaMetadata.participationTractAc)
        : (formulaMetadata.participationTractAc ?? 0)
      : 0;
  const uatGisAc = formulaMetadata.uatGisAc ?? 0;

  if (activeAllocationMethod === 'lateral_length') {
    const totalLlFt =
      (formulaMetadata as LateralUnitInterestFormula).totalLlFt ?? 0;
    const uatFt = (formulaMetadata as LateralUnitInterestFormula).uatFt ?? 0;

    return (
      <div
        data-testid={isWI ? 'unit-wi-calculation' : 'unit-nri-calculation'}
        className={style.formulaCalculation}
      >
        {formatNumberWithTooltip(tractNri, 8, '0.00000000', false)} * (
        {formatNumberWithTooltip(uatFt, 2, '0.00', false, 'ft')} /{' '}
        {formatNumberWithTooltip(totalLlFt, 2, '0.00', false, 'ft')}) * (
        {formatNumberWithTooltip(participationTractAc, 2, '0.00', false, 'ac')}{' '}
        / {formatNumberWithTooltip(uatGisAc, 2, '0.00', false, 'ac')}) ={' '}
        {formatNumberWithTooltip(unitInterest, 8, '0.00000000', false)}
      </div>
    );
  } else {
    const uatAc = (formulaMetadata as AcreageUnitInterestFormula).uatAc ?? 0;
    const unitTractGrossAcresAc =
      (formulaMetadata as AcreageUnitInterestFormula).unitTractGrossAcresAc ??
      0;

    return (
      <div
        data-testid={isWI ? 'unit-wi-calculation' : 'unit-nri-calculation'}
        className={style.formulaCalculation}
      >
        {formatNumberWithTooltip(tractNri, 8, '0.00000000', false)} * (
        {formatNumberWithTooltip(uatAc, 2, '0.00', false, 'ac')} /{' '}
        {formatNumberWithTooltip(unitTractGrossAcresAc, 2, '0.00', false, 'ac')}
        ) * (
        {formatNumberWithTooltip(participationTractAc, 2, '0.00', false, 'ac')}{' '}
        / {formatNumberWithTooltip(uatGisAc, 2, '0.00', false, 'ac')}) ={' '}
        {formatNumberWithTooltip(unitInterest, 8, '0.00000000', false)}
      </div>
    );
  }
}
